import { useParams, useNavigate } from 'react-router-dom';
import { useAdminGetOrganization } from '@/api/auth/admin/admin';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import { OrganizationFlagsForm } from '@/components/admin/OrganizationFlagsForm';
import { PartnerAssociationsForm } from '@/components/admin/PartnerAssociationsForm';
import { OrganizationFeaturesForm } from '@/components/admin/OrganizationFeaturesForm';
import { ConsultantHoursCard } from '@/components/admin/ConsultantHoursCard';
import { OrganizationEntitlementsTable } from '@/components/admin/OrganizationEntitlementsTable';
import { OrganizationRegistrationsTable } from '@/components/admin/OrganizationRegistrationsTable';
import { formatDateTime } from '@/lib/format';

/**
 * Admin Organization detail page. Shows the organization record and lets an
 * operator edit its pricing-tier flags and partner associations. Entitlement
 * and product-registration views are added in a following section.
 */
export default function AdminOrganizationDetailPage() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();

  const {
    data: orgData,
    isLoading,
    error,
  } = useAdminGetOrganization(organizationId!, {
    query: { enabled: !!organizationId },
  });

  const organization = orgData?.data;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  if (error || !organization) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/organizations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
        <div className="text-center text-destructive py-8">
          <p>
            Failed to load organization: {(error as Error)?.message || 'Organization not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/dashboard/admin/organizations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold">Organization: {organization.name}</h1>
        <p className="text-muted-foreground">
          View and manage this organization's flags, partner associations, consultant hours,
          entitlements, and product registrations
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Organization Information</CardTitle>
          <CardDescription>Core details for this organization</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-sm font-medium">Organization ID</p>
            <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] text-sm font-mono">
              {organization.id}
            </code>
          </div>
          <div>
            <p className="text-sm font-medium">Name</p>
            <p className="text-lg">{organization.name}</p>
          </div>
          <div>
            <p className="text-sm font-medium">Created</p>
            <p className="text-lg">{formatDateTime(organization.createdUtc)}</p>
          </div>
          <div>
            <p className="text-sm font-medium">Last Updated</p>
            <p className="text-lg">{formatDateTime(organization.updatedUtc)}</p>
          </div>
          <div>
            <p className="text-sm font-medium">Billing Country</p>
            <p className="text-lg">{organization.billingCountry || '—'}</p>
          </div>
          <div>
            <p className="text-sm font-medium">Partner Identifier</p>
            {organization.partnerIdentifier ? (
              <code className="relative rounded bg-muted px-[0.3rem] py-[0.2rem] text-sm font-mono">
                {organization.partnerIdentifier}
              </code>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <OrganizationFlagsForm organization={organization} />
        <PartnerAssociationsForm organization={organization} />
      </div>

      <OrganizationFeaturesForm organization={organization} />

      <ConsultantHoursCard organization={organization} />

      <OrganizationEntitlementsTable organizationId={organization.id} />
      <OrganizationRegistrationsTable organization={organization} />
    </div>
  );
}
